<template>
  <b-form @submit.prevent="submit">
    <b-row>
      <b-col cols="12" class="h5 font-weight-bold mb-4"> Edit Summary of this Learning Unit </b-col>

      <!-- Summary Overview -->
      <b-col cols="12" class="mb-4">
        <label for="input__mc-lu-overview" class="mb-u4"> Learning unit overview </label>
        <base-text-editor
          id="input__mc-lu-overview"
          v-model="$v.form.summary_overview.$model"
          name="input__mc-lu-overview"
          :class="{
            text_editor__required: $v.form.summary_overview.$anyError
          }"
          :disabled="disabled"
          placeholder="Insert your learning unit overview "
        ></base-text-editor>
        <div
          v-if="$v.form.summary_overview.$anyError"
          id="input__mc-lu-overview-invalid-feedback"
          class="text_feedback"
        >
          The Learning unit overview is a required field.
        </div>
      </b-col>

      <!-- Summary Outcomes -->
      <b-col cols="12" class="mb-4">
        <label for="input__mc-outcomes" class="mb-u4"> Outcomes </label>
        <base-text-editor
          id="input__mc-outcomes"
          v-model="$v.form.summary_outcome.$model"
          name="input__mc-outcomes"
          :class="{
            text_editor__required: $v.form.summary_outcome.$anyError
          }"
          :disabled="disabled"
          placeholder="Insert your outcomes "
        ></base-text-editor>
        <div
          v-if="$v.form.summary_outcome.$anyError"
          id="input__mc-outcomes-invalid-feedback"
          class="text_feedback"
        >
          The Outcomes is a required field.
        </div>
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <b-col cols="12" class="h5 font-weight-bold mb-4">Content example</b-col>

      <!-- Content -->
      <b-col cols="12" class="mb-4">
        <label for="input__content" class="mb-u4"> Description </label>
        <base-text-editor
          id="input__content"
          v-model="$v.form.content.$model"
          name="input__content"
          :class="{
            text_editor__required: $v.form.content.$anyError
          }"
          :disabled="disabled"
          placeholder="Insert your description"
        ></base-text-editor>
        <div
          v-if="$v.form.content.$anyError"
          id="input__content-invalid-feedback"
          class="text_feedback"
        >
          The Description is a required field.
        </div>
      </b-col>

      <!-- Content media -->
      <b-col cols="12">
        <base-upload-media
          id="input__content_media"
          ref="contentMediaUploader"
          key="contentMediaUploader"
          class="mb-4"
          :required="false"
          :reference-meta="referenceMeta"
          field-upload="content_media"
          :file.sync="formMedia.content_media"
          :has-file.sync="file.content_media"
          :disabled="disabled"
          @upload:video="
            (v) => {
              video.media_content = v;
            }
          "
        >
          <template v-if="isUploadContentMedia" slot="progress-bar">
            <base-progress-bar-video-upload class="mt-3" :percentage="percentageContentMedia" />
          </template>
        </base-upload-media>
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <!-- Button -->
      <b-col cols="12" class="mb-4">
        <b-button variant="success" class="btn__create-mc mr-2" :disabled="disabled" type="submit">
          <div v-if="disabled" class="mb-u1">
            <b-spinner label="Loading..." variant="secondary" small />
          </div>
          <div v-else>
            {{ learningUnit && learningUnit.id ? 'Save' : 'Create' }}
          </div>
        </b-button>

        <b-button
          variant="link"
          class="btn__back"
          :disabled="disabled"
          @click="$_pageNavigationMixin_gotoPreviousPage"
          >Back to previous page
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import BaseTextEditor from '@/components/base-components/BaseTextEditor';
import BaseUploadMedia from '@/components/base-components/BaseUploadMedia';
import BaseProgressBarVideoUpload from '@/components/base-components/BaseProgressBarVideoUpload';
import LearningUnit from '@/models/LearningUnit';
import Vimeo from '@/models/Vimeo';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { notificationMixin } from '@/mixins/notificationMixin';
import * as tus from 'tus-js-client';
import { useFileSizeConverter } from '@/composables/useFileSizeConverter';
import { pageNavigationMixin } from '@/mixins/pageNavigationMixin';
const { formatMegaBytes } = useFileSizeConverter();

export default {
  components: {
    BaseTextEditor,
    BaseUploadMedia,
    BaseProgressBarVideoUpload
  },
  mixins: [userInfoMixin, notificationMixin, pageNavigationMixin],

  props: {
    learningUnit: {
      type: LearningUnit,
      default: null
    }
  },

  data() {
    return {
      disabled: false,
      onSubmit: false,
      form: {
        summary_overview: '',
        summary_outcome: '',
        content: '',
        content_media_vimeo: null
      },
      formMedia: {
        content_media: []
      },
      file: {
        content_media: false
      },
      isNameDuplicate: false,
      referenceMeta: {
        ref: 'learning-unit',
        refId: null
      },
      video: {
        media_content: null,
        upload_link_vimeo_content: null,
        video_link_vimeo_content: null
      },
      isUploadContentMedia: false,
      percentageMediaDetail: 0,
      percentageContentMedia: 0
    };
  },

  validations: {
    form: {
      summary_overview: {},
      summary_outcome: {},
      content: {}
    }
  },

  mounted() {
    this.initMicroCredential();
  },

  methods: {
    initMicroCredential() {
      if (!this.learningUnit) return;

      const { learningUnit, form } = this;

      for (const [key] of Object.entries(form)) {
        this.form[key] = learningUnit[key];
      }

      const { id, content_media_vimeo_id, content_media = [] } = this.learningUnit;

      this.$set(this.referenceMeta, 'refId', id);

      if (content_media_vimeo_id) {
        const video = Vimeo.find(content_media_vimeo_id);
        this.$set(this.form, 'content_media_vimeo', content_media_vimeo_id);
        this.$set(this.formMedia, 'content_media', [video]);
        this.video.media_content = video;
        this.video.video_link_vimeo_content = video.video_link;
      }

      if (content_media.length) {
        this.$set(this.formMedia, 'content_media', content_media);
      }
    },

    async resumableUpload(video, params = {}, type = '') {
      const body = {
        ...params,
        size: video.size
      };

      try {
        const { response } = await Vimeo.api().getVimeoUploadLink(body);
        let upload_link = '';
        if (response) {
          upload_link = response.data.upload_link;

          if (type == 'content') {
            this.$set(this.video, `upload_link_vimeo_content`, response.data.upload_link);
            this.$set(this.video, `video_link_vimeo_content`, response.data.link);
          }
        } else {
          console.error('response is ', response);
          throw 'ERROR : form_based_viemo is not exist';
        }

        const responseTusUpload = await new Promise((resolve) => {
          let upload = new tus.Upload(video, {
            uploadUrl: upload_link,
            retryDelays: [0, 3000, 5000, 10000, 20000],
            uploadSize: video.size,
            metadata: {
              filename: video.name,
              filesize: video.size,
              filetype: video.type
            },
            onError: function (error) {
              console.log('Failed because: ' + error);
              this.isLoading = false;
              this.notiError();
            },

            // Callback for reporting upload progress
            onProgress: (bytesUploaded, bytesTotal) => {
              // if (type == "detail") {
              //   this.percentageMediaDetail = (
              //     (bytesUploaded / bytesTotal) *
              //     100
              //   ).toFixed(2);
              // }

              if (type == 'content') {
                this.percentageContentMedia = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
              }
            },

            // Callback for once the upload is completed
            onSuccess: async () => {
              const minInterval = 30;
              const baseSize = 5;
              const sizeMB = formatMegaBytes(video.size, 0);
              const numInterval = Math.ceil(sizeMB / baseSize) + 1; // calculate num interval of video from total size and base size, plus 1 interval

              await Vimeo.api().getVideoInformation(response.data.id, {
                numInterval: numInterval < minInterval ? minInterval : numInterval
              });

              resolve(upload);
            }
          });

          upload.findPreviousUploads().then(function (previousUploads) {
            // Found previous uploads so we select the first one.
            if (previousUploads.length) {
              upload.resumeFromPreviousUpload(previousUploads[0]);
            }

            // Start the upload
            upload.start();
          });
        });

        this.isUploadContentMedia = false;
        return responseTusUpload;
      } catch (error) {
        this.isLoading = false;
        this.notiError();
      }
    },

    async submit() {
      this.$v.form.$touch();

      this.onSubmit = true;

      const form = { ...this.form };

      try {
        this.disabled = true;

        // if new content detail was uploaded
        if (
          this.$refs.contentMediaUploader &&
          this.$refs.contentMediaUploader.$children[0].hasAnyQueuedFiles()
        ) {
          // if there're old media detail, set media_detail to empty array to clean old file relation.
          if (this.formMedia.content_media.length) {
            this.$set(form, 'content_media', []);
          }

          // if there's old vimeo, set content_media_vimeo to null to clean old vimeo relation.
          if (form.content_media_vimeo !== null) {
            this.$set(form, 'content_media_vimeo', null);
          }
        }

        const {
          response: { data }
        } = await LearningUnit.api().updateLearningUnit(this.learningUnit.id, {
          ...form
        });

        const { id } = data;

        if (this.$refs.contentMediaUploader.$children[0].hasAnyDeletedFiles()) {
          await this.$refs.contentMediaUploader.$children[0].deleteRemovedFiles();
        }

        if (this.video.media_content && !this.video.media_content.id) {
          // trigger upload video when only new video was attached.
          this.isUploadContentMedia = true;
          await this.resumableUpload(
            this.video.media_content,
            {
              learning_unit_content_id: id,
              learning_unit_detail_id: null
            },
            'content'
          );
        } else if (this.$refs.contentMediaUploader.$children[0].hasAnyQueuedFiles()) {
          await this.$refs.contentMediaUploader.$children[0].upload();
        }

        this.notiSuccessDefault(`The Micro-Credentials was successfully updated`);

        this.onSubmit = false;

        this.$_pageNavigationMixin_gotoPreviousPage();
      } catch (error) {
        this.notiError();
      } finally {
        this.disabled = false;
      }
    }
  }
};
</script>

<style lang="scss">
.text_editor__required {
  .ql-container.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 0 0 4.64px 4.64px !important;
  }

  .ql-toolbar.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 4.64px 4.64px 0 0 !important;
    border-bottom: none !important;
  }
}

.text_feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f83245;
}
</style>
